import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Heart } from 'lucide-react';
import { useWishlist } from '../../context/WishlistContext';

const navLinks = [
  { label: 'Gallery', href: '#gallery' },
  { label: 'About', href: '#about' },
  { label: 'Contact', href: '#contact' }
];

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { wishlistCount, openWishlist } = useWishlist();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        className={`nav-container ${isScrolled ? 'scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <motion.a
            href="#"
            className="font-cinzel text-xl md:text-2xl text-gold-gradient"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            whileHover={{ scale: 1.05 }}
            data-testid="logo-link"
          >
            Ethereal Calligraphy
          </motion.a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <motion.button
                key={link.label}
                className="font-manrope text-sm text-gray-300 hover:text-[#D4AF37] transition-colors tracking-wide"
                onClick={() => scrollToSection(link.href)}
                whileHover={{ y: -2 }}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </motion.button>
            ))}

            {/* Wishlist Button */}
            <motion.button
              onClick={openWishlist}
              className="relative flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] font-manrope text-sm hover:bg-[#D4AF37]/20 transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="nav-wishlist-btn"
            >
              <Heart size={16} className={wishlistCount > 0 ? 'fill-current' : ''} />
              <span>Wishlist</span>
              {wishlistCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#D4AF37] text-black text-xs font-bold flex items-center justify-center"
                >
                  {wishlistCount}
                </motion.span>
              )}
            </motion.button>
          </div>

          {/* Mobile: Wishlist + Menu */}
          <div className="flex md:hidden items-center gap-3">
            {/* Mobile Wishlist */}
            <button
              onClick={openWishlist}
              className="relative p-2 text-gray-300 hover:text-[#D4AF37] transition-colors"
              data-testid="mobile-wishlist-btn"
            >
              <Heart size={22} className={wishlistCount > 0 ? 'fill-[#D4AF37] text-[#D4AF37]' : ''} />
              {wishlistCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#D4AF37] text-black text-[10px] font-bold flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </button>

            {/* Mobile Menu Button */}
            <button
              className="p-2 text-gray-300 hover:text-[#D4AF37] transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-50 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/90 backdrop-blur-xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Menu Content */}
            <motion.div
              className="absolute inset-x-0 top-0 p-6"
              initial={{ y: -20 }}
              animate={{ y: 0 }}
              exit={{ y: -20 }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-12">
                <span className="font-cinzel text-xl text-gold-gradient">Ethereal Calligraphy</span>
                <button
                  className="p-2 text-gray-300 hover:text-[#D4AF37]"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Links */}
              <div className="flex flex-col gap-6">
                {navLinks.map((link, index) => (
                  <motion.button
                    key={link.label}
                    className="font-cinzel text-2xl text-gray-200 hover:text-[#D4AF37] transition-colors text-left"
                    onClick={() => scrollToSection(link.href)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {link.label}
                  </motion.button>
                ))}

                {/* Mobile Wishlist Link */}
                <motion.button
                  className="font-cinzel text-2xl text-[#D4AF37] hover:text-[#F4C430] transition-colors text-left flex items-center gap-3"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    openWishlist();
                  }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: navLinks.length * 0.1 }}
                >
                  <Heart size={24} className={wishlistCount > 0 ? 'fill-current' : ''} />
                  Wishlist {wishlistCount > 0 && `(${wishlistCount})`}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
